import * as React from 'react';
import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { useTheme, useMediaQuery, alpha } from '@mui/material';
import FeedbackIcon from '@mui/icons-material/Feedback';
import SendIcon from '@mui/icons-material/Send';
import {saveFeedbackForm,feedbackNotInterestedStatusChange} from '../../services/feedBackService'

// Sub-questions mapping based on type
const subQuestionsMap = {
  DELIVERY: [
    { label: "Food Quality", value: "food_quality" },
    { label: "Packaging", value: "packaging" },
    { label: "Delivery Timing", value: "delivery_timing" },
  ],
  TAKEAWAY: [
    { label: "Food Quality", value: "food_quality" },
    { label: "Staff Courtesy", value: "staff_courtesy" },
    { label: "Hygiene", value: "hygiene" },
  ],
  RESERVATION: [
    { label: "Food Quality", value: "food_quality" },
    { label: "Staff Courtesy", value: "staff_courtesy" },
    { label: "Hygiene", value: "hygiene" },
    { label: "Ease of Booking", value: "ease_of_booking" },
  ],
  CATERING: [
    { label: "Food Quality", value: "food_quality" },
    { label: "Presentation", value: "presentation" },
    { label: "Professionalism", value: "professionalism" },
  ],
};

// Star Component
const StarRating = ({ rating, onRatingChange, size = 24, interactive = true }) => {
  const [hover, setHover] = useState(0);

  return (
    <Box sx={{ 
      display: 'flex', 
      gap: 0.5, 
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height:80
    }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Box
          key={star}
          component="span"
          sx={{
            fontSize: size,
            cursor: interactive ? 'pointer' : 'default',
            color: star <= (hover || rating) ? '#FFB800' : '#E0E0E0',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: interactive && star <= (hover || rating) ? 'scale(1.1)' : 'scale(1)',
            '&:hover': interactive ? {
              transform: 'scale(1.2)',
              color: '#FFA000'
            } : {},
            filter: star <= (hover || rating) ? 'drop-shadow(0 0 8px rgba(255, 184, 0, 0.3))' : 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            msUserSelect: 'none',
          }}
          onClick={() => {
            if (interactive && onRatingChange) {
              onRatingChange(star);
            }
          }}
          onMouseEnter={() => {
            if (interactive) {
              setHover(star);
            }
          }}
          onMouseLeave={() => {
            if (interactive) {
              setHover(0);
            }
          }}
        >
          ★
        </Box>
      ))}
    </Box>
  );
};

export default function FeedbackModal({ 
  modalOpen = false, 
  setModalOpen = () => {}, 
  type = 'Delivery', 
  onSubmit = () => {},
  onClose = () => {},
  modalWidth = false,
  session_id,
  feedbackDetails,
  feedBackAllData
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [overallRating, setOverallRating] = useState(0);
  const [subRatings, setSubRatings] = useState({});
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  // Static order data
  const orderData = {
    orderId: 'ORD-2026-0789',
    date: 'July 3, 2026',
    time: '7:30 PM',
    items: [
      { name: 'Margherita Pizza', quantity: 2, price: '$12.99' },
      { name: 'Caesar Salad', quantity: 1, price: '$8.50' },
      { name: 'Garlic Bread', quantity: 1, price: '$5.99' },
    ],
    total: '$40.47',
    status: 'Completed',
    deliveryAddress: '123 Main Street, Apt 4B, New York, NY 10001',
    specialInstructions: 'Extra cheese on pizza, no onions'
  };

  // Ensure type is valid and has a fallback
  const validTypes = ['DELIVERY', 'TAKEAWAY', 'RESERVATION', 'CATERING'];
  const currentType = validTypes.includes(type) ? type : 'DELIVERY';
  const subQuestions = subQuestionsMap[currentType] || [];

  // Handle scroll lock
  useEffect(() => {
    if (modalOpen) {
      setAnimateIn(true);
      // Lock scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      // Unlock scroll
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    // console.log("Feedback All Data => ", feedbackDetails)
    
    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };


  }, [modalOpen]);

  const handleOverallRating = (value) => {
    setOverallRating(value);
    setError('');
    if (value === 0) {
      setSubRatings({});
    }
  };

  const handleSubRating = (question, value) => {
    setSubRatings(prev => ({
      ...prev,
      [question]: value
    }));
  };  

  const handleSubmit = async () => {
    if (overallRating === 0) {
      setError('Please provide an overall rating');
      return;
    }

    setIsSubmitting(true);
    
    const feedbackData = {
      ...subRatings,
      guest_id: localStorage.getItem("device_uuid"),
      user_session_id: session_id,
      overall_rating: overallRating,
      feedback_type: currentType,
      comments: notes,
      timestamp: new Date().toISOString(),
    };

    // console.log()

    try {
      const response = await saveFeedbackForm(feedbackData);
      
      // Reset form
      setOverallRating(0);
      setSubRatings({});
      setNotes('');
      setError('');
      setAnimateIn(false);
      
      setTimeout(() => {
        setModalOpen(false);
        onClose();
        setIsSubmitting(false);
      }, 300);
    } catch (error) {
      // console.error('Error submitting feedback:', error);
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setModalOpen(false);
  };

  const handleNotInterested = async() => {
    let guest_id = localStorage.getItem("device_uuid");
    
    let payload = {
      "guest_id":guest_id
    }
    if(feedBackAllData.order_id) payload["order_id"] = feedBackAllData.order_id
    else if (feedBackAllData.catering_request_id) payload["request_id"] = feedBackAllData.catering_request_id
    else if (feedBackAllData.reservation_id) payload["reservation_id"] = feedBackAllData.reservation_id

    // console.log("Feed Back Details => ", feedbackDetails)
    setModalOpen(false)
    try{
      const response = await feedbackNotInterestedStatusChange(payload);
    }
    catch(error){

    }
  }

  // Modal style
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isMobile ? '92vw' : isTablet ? '85vw' : modalWidth ? modalWidth : 640,
    maxWidth: '92vw',
    maxHeight: '90vh',
    bgcolor: '#FFFFFF',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.02)',
    p: isMobile ? 3 : 4,
    overflow: 'auto',
    opacity: animateIn ? 1 : 0,
    transform: animateIn ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0.95)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:focus': {
      outline: 'none',
    },
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#F5F5F5',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#D1D5DB',
      borderRadius: '10px',
      '&:hover': {
        background: '#9CA3AF',
      },
    },
  };

  // Gradient background for header
  const getTypeGradient = () => {
    const gradients = {
      'DELIVERY': `linear-gradient(135deg, #680000 0%, #8B0000 100%)`,
      'TAKEAWAY': `linear-gradient(135deg, #680000 0%, #8B0000 100%)`,
      'RESERVATION': `linear-gradient(135deg, #680000 0%, #8B0000 100%)`,
      'CATERING': `linear-gradient(135deg, #680000 0%, #8B0000 100%)`
    };
    return gradients[currentType] || gradients['DELIVERY'];
  };

  const getTypeEmoji = () => {
    const emojis = {
      'DELIVERY': '🚚',
      'TAKEAWAY': '🍽️',
      'RESERVATION': '📅',
      'CATERING': '🎉'
    };
    return emojis[currentType] || '⭐';
  };

  const getTypeDisplayName = () => {
    const names = {
      'DELIVERY': 'Delivery',
      'TAKEAWAY': 'Takeaway',
      'RESERVATION': 'Reservation',
      'CATERING': 'Catering'
    };
    return names[currentType] || currentType;
  };

  return (
    <Modal
      open={modalOpen}
      onClose={handleClose}
      aria-labelledby="feedback-modal-title"
      aria-describedby="feedback-modal-description"
      sx={{
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        borderRadius:'6px'
      }}
      // Ensure scroll lock is enabled (default is true, but explicitly set for clarity)
      disableScrollLock={false}
      // Keep the modal mounted to maintain scroll lock
      keepMounted={false}
    >
      <Box sx={style}>
        {/* Decorative gradient bar */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: getTypeGradient(),
            borderRadius:'6px'
            // borderTopLeftRadius: '20px',
            // borderTopRightRadius: '20px',
          }}
        />

        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: '#9CA3AF',
            bgcolor: '#F3F4F6',
            '&:hover': {
              color: '#374151',
              bgcolor: '#E5E7EB',
              transform: 'rotate(90deg)',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 1,
            width: 40,
            height: 40,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, pr: 4 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: getTypeGradient(),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            {getTypeEmoji()}
          </Box>
          <Box>
            <Typography 
              id="feedback-modal-title" 
              variant="h5" 
              component="h2" 
              sx={{ 
                fontWeight: 700,
                color: '#1F2937',
                letterSpacing: '-0.01em',
                lineHeight: 1.2,
              }}
            >
              Share Your Feedback
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#6B7280',
                fontWeight: 500,
              }}
            >
              Help us improve your {getTypeDisplayName().toLowerCase()} experience
            </Typography>
          </Box>
        </Box>

        {/* Order Details Section */}
        <Box 
          sx={{ 
            mt: 3, 
            mb: 3,
            bgcolor: alpha('#680000', 0.04),
            borderRadius: '12px',
            border: `1px solid ${alpha('#680000', 0.1)}`,
            overflow: 'hidden',
          }}
        >
          {/* Order Header */}
          <Box 
            sx={{ 
              p: 2, 
              bgcolor: alpha('#680000', 0.06),
              borderBottom: `1px solid ${alpha('#680000', 0.1)}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 500, letterSpacing: '0.5px' }}>
                DETAILS
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#1F2937' }}>
                {currentType === "CATERING" ? `Event Name : ${feedbackDetails?.event_name}` : 
                 currentType === "RESERVATION" ? `Reservation Number : ${feedbackDetails?.reservation_number}` : 
                 `Order Number : ${feedbackDetails?.order_number}`}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#1F2937' }}>
                {feedbackDetails?.customer_name}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600, color: '#1F2937' }}>
                Outlet : {feedbackDetails?.outlet}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                {type === "CATERING" ? feedbackDetails?.event_date : 
                 type === "RESERVATION" ? feedbackDetails?.date : 
                 feedbackDetails?.created_at}
              </Typography>
              <Box 
                sx={{ 
                  bgcolor: '#10B981', 
                  color: 'white', 
                  px: 1.5, 
                  py: 0.5, 
                  borderRadius: '50px',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  letterSpacing: '0.3px',
                  textTransform: 'uppercase'
                }}
              >
                {orderData.status}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Type Badge */}
        <Box 
          sx={{ 
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: alpha('#680000', 0.08),
            px: 2,
            py: 0.5,
            borderRadius: '50px',
            fontSize: '0.8rem',
            fontWeight: 600,
            color: '#680000',
            mt: 1,
            mb: 3,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          <FeedbackIcon sx={{ fontSize: 16 }} />
          {getTypeDisplayName()}
        </Box>

        {/* Overall Rating Section */}
        <Box sx={{ mb: 3.5 }}>
          <Typography variant="subtitle1" sx={{ 
            fontWeight: 600, 
            mb: 1.5, 
            color: '#1F2937',
            textAlign: 'center',
          }}>
            Overall Rating
          </Typography>
          <Box 
            sx={{ 
              bgcolor: '#FAFAFA',
              p: 2,
              borderRadius: '12px',
              border: '1px solid #F3F4F6',
              transition: 'border-color 0.2s',
              '&:hover': {
                borderColor: '#E5E7EB',
              },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <StarRating 
              rating={overallRating} 
              onRatingChange={handleOverallRating}
              size={36}
            />
            {overallRating > 0 && (
              <Typography 
                variant="caption" 
                sx={{ 
                  display: 'block', 
                  mt: 0.5,
                  color: '#6B7280',
                  fontWeight: 500,
                  textAlign: 'center',
                }}
              >
                {overallRating === 1 && 'Needs Improvement'}
                {overallRating === 2 && 'Below Average'}
                {overallRating === 3 && 'Average'}
                {overallRating === 4 && 'Good'}
                {overallRating === 5 && 'Excellent!'}
              </Typography>
            )}
            {error && (
              <Typography color="error" variant="caption" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                {error}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Sub Ratings Section */}
        {overallRating > 0 && subQuestions.length > 0 && (
          <Box sx={{ mb: 3.5 }}>
            <Divider sx={{ mb: 2.5 }} />
            <Typography variant="subtitle2" sx={{ 
              color: '#6B7280', 
              mb: 2,
              fontWeight: 500,
              textAlign: 'center',
            }}>
              <span style={{ fontSize: '18px' }}>✨</span> Rate specific aspects
              <span style={{ fontWeight: 400, fontSize: '0.8rem', color: '#9CA3AF', marginLeft: '4px' }}>(optional)</span>
            </Typography>
            
            {subQuestions.map((question, index) => (
              <Box 
                key={question.value} 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  py: 1.5,
                  borderBottom: index < subQuestions.length - 1 ? '1px solid #F3F4F6' : 'none',
                  '&:hover': {
                    bgcolor: '#FAFAFA',
                    borderRadius: '8px',
                  },
                  transition: 'all 0.2s',
                }}
              >
                <Typography variant="body2" sx={{ 
                  color: '#374151',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  mb: 0.5,
                }}>
                  {question.label}
                </Typography>
                <StarRating 
                  rating={subRatings[question.value] || 0} 
                  onRatingChange={(value) => handleSubRating(question.value, value)}
                  size={28}
                />
              </Box>
            ))}
            <Divider sx={{ mt: 2.5 }} />
          </Box>
        )}

        {/* Notes Section */}
        <Box sx={{ mb: 3.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: '#1F2937' }}>
            Additional Comments <span style={{ color: '#9CA3AF', fontWeight: 400, fontSize: '0.85rem' }}>(optional)</span>
          </Typography>
          <TextareaAutosize
            minRows={3}
            maxRows={6}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Share more details about your experience..."
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              fontFamily: 'inherit',
              fontSize: '0.95rem',
              resize: 'vertical',
              boxSizing: 'border-box',
              backgroundColor: '#FAFAFA',
              transition: 'all 0.2s',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#680000';
              e.target.style.backgroundColor = '#FFFFFF';
              e.target.style.boxShadow = '0 0 0 4px rgba(104, 0, 0, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#E5E7EB';
              e.target.style.backgroundColor = '#FAFAFA';
              e.target.style.boxShadow = 'none';
            }}
          />
        </Box>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 2, 
          mt: 2,
          pt: 2,
          borderTop: '1px solid #F3F4F6',
        }}>
          <Button 
            variant="outlined" 
            onClick={handleNotInterested}
            disabled={isSubmitting}
            sx={{ 
              color: '#680000',
              borderColor:'#680000',
              fontWeight: 500,
             '&:hover': {
                background: 'linear-gradient(135deg, #4d0000 0%, #680000 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(104, 0, 0, 0.3)',
                color:'white'
              },
              textTransform: 'none',
              fontSize: '0.95rem',
              px: 3,
            }}
          >
            Not Interested
          </Button>
          {/* <Button 
            variant="text" 
            onClick={handleClose}
            disabled={isSubmitting}
            sx={{ 
              color: '#6B7280',
              fontWeight: 500,
              '&:hover': {
                color: '#374151',
                bgcolor: '#F3F4F6',
              },
              textTransform: 'none',
              fontSize: '0.95rem',
              px: 3,
            }}
          >
            Cancel
          </Button> */}
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={isSubmitting}
            endIcon={<SendIcon />}
            sx={{
              background: 'linear-gradient(135deg, #680000 0%, #8B0000 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #4d0000 0%, #680000 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(104, 0, 0, 0.3)',
              },
              '&:active': {
                transform: 'scale(0.98)',
              },
              '&:disabled': {
                background: '#D1D5DB',
              },
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 600,
              px: 4,
              py: 1.2,
              borderRadius: '12px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}